import { useState } from 'react';
import { cx } from 'class-variance-authority';
import * as v from 'valibot';

import { DataTable, Pagination, Spinner, Text } from '@nvision/ui';

import { Card } from '@/components/card';

import { useContractHistory } from '../../../hooks';
import { type ContractHistoryParams, contractHistoryParamsSchema } from '../../../models';

import styles from './contract-history.module.css';

interface ContractHistoryProps {
  contractKey?: string;
}

const columns = [
  {
    accessorKey: 'createdOn',
    header: 'Created On',
    size: 160,
  },
  {
    accessorKey: 'userName',
    header: 'User Name',
    size: 180,
  },
  {
    accessorKey: 'action',
    header: 'Action',
    size: 160,
  },
  {
    accessorKey: 'comment',
    header: 'Comment',
    size: 360,
  },
];

export const ContractHistory: React.FC<ContractHistoryProps> = ({ contractKey }) => {
  const [params, setParams] = useState<ContractHistoryParams>({
    ...v.getDefaults(contractHistoryParamsSchema),
    contractKey: contractKey ?? '',
  });
  const { data: history, isFetched, isLoading } = useContractHistory(params);
  const hasData = (history?.totalCount ?? 0) > 0;
  const isOpen = isLoading || hasData;

  return (
    <Card className={styles.card} collapsible defaultIsOpen={isOpen} key={isFetched ? `ready-${hasData}` : 'loading'}>
      <Card.Header title="History" />
      <Card.Main>
        {isLoading ? (
          <div className={cx(styles.tableContainer, styles.loader)}>
            <Spinner size="lg" />
          </div>
        ) : hasData ? (
          <>
            <div className={styles.tableContainer}>
              <DataTable
                columns={columns}
                data={history?.items || []}
                limit={params.limit}
                page={params.offset / params.limit + 1}
                totalItems={history?.totalCount}
                translations={{ noData: 'No data found.' }}
              />
            </div>
            <Pagination
              limit={params.limit}
              onLimitChange={(limit: number) => setParams((prev) => ({ ...prev, limit, offset: 0 }))}
              onOffsetChange={(offset: number) => setParams((prev) => ({ ...prev, offset }))}
              onPageChange={(page: number) => {
                setParams((prev) => ({ ...prev, offset: (page - 1) * params.limit }));
              }}
              page={params.offset / params.limit + 1}
              totalItems={history?.totalCount || 0}
            />
          </>
        ) : (
          <Text className={styles.emptyState} size="sm">
            No data found.
          </Text>
        )}
      </Card.Main>
    </Card>
  );
};
